import { IAddressData } from '../models/leads/leads.model';
import { ResourceCountry, ResourceProvinces } from '../models/resources/resources.model';

export class AddressHelper {
  private static resolveCountryFunction: (value: (ResourceCountry[])) => void;
  private static resolveProvinceFunction: (value: (ResourceProvinces[])) => void;
  private static readonly promiseCountry = AddressHelper.initCountryPromise();
  private static readonly promiseProvince = AddressHelper.initProvincePromise();
  private static readonly provinceCountryCodesPromise = AddressHelper.initProvinceCountryCodesPromise();
  private static readonly promiseProvincesForEachCountry = new Map<string, Promise<ResourceProvinces[]>>();
  private static loadedCountries: ResourceCountry[];
  private static loadedProvinces: ResourceProvinces[];

  private static initCountryPromise(): Promise<ResourceCountry[]> {
    return new Promise<ResourceCountry[]>((resolve) => {
      this.resolveCountryFunction = resolve;
    });
  }

  private static initProvinceCountryCodesPromise(): Promise<string[]> {
    return new Promise<string[]>((resolve) => {
      this.promiseProvince.then((value) => {
        const countryCodes = new Set<string>();

        for (const province of value) {
          if (province.CountryCode) {
            countryCodes.add(province.CountryCode.toUpperCase());
          }
        }

        resolve([...countryCodes]) ;
      });
    });
  }

  private static initProvincePromise(): Promise<ResourceProvinces[]> {
    return new Promise<ResourceProvinces[]>((resolve) => {
      this.resolveProvinceFunction = resolve;
    });
  }

  static getLeadAddress(address: IAddressData, province: String = '', skipCountry = false): string {
    let country = address?.CountryCode && this.loadedCountries ?
      this.loadedCountries.find(item => item.IsoCode === address?.CountryCode.toUpperCase())?.Name : undefined;
    if(skipCountry){
      country = '';
    }
    return `${address?.Commission ? '<span class="comission">' + address?.Commission + '</span>,' : ''} ${address?.Address ?? ''}, ${address?.ZipCode ?? ''} ${address?.City ?? ''}, ${ province && country ? province + ',' : '' } ${ country ?? '' }`
      .replace(/^[\s,]+|[\s,]+$/gm, '')
      .replace(/(\s*,\s*)+/gm, ', ')
      .replace(/\s+/gm, ' ');
  }

  static getMapUrl(address: IAddressData): string {
    const country = address?.CountryCode && this.loadedCountries ?
      this.loadedCountries.find(item => item.IsoCode === address?.CountryCode.toUpperCase())?.Name : undefined;

    const query = `${address?.Address ?? ''} ${address?.ZipCode ?? ''} ${address?.City ?? ''} ${country ?? ''}`.trim();
    const userAgent = navigator.userAgent.toLowerCase();

    const isIOSPlatform = userAgent.includes('ipad') || userAgent.includes('iphone') || userAgent.includes('ipod');
    const mapAppleUrl = 'https://maps.apple.com/place?address=';
    const mapGoogleUrl = 'https://www.google.com/maps/search/?api=1&query=';
    return isIOSPlatform ? mapAppleUrl + encodeURI(query) : mapGoogleUrl + encodeURI(query);
  }

  static getCountries(): Promise<ResourceCountry[]> {
    return this.promiseCountry;
  }

  static getProvinceCountryCodes(): Promise<string[]> {
    return this.provinceCountryCodesPromise;
  }

  static getProvinces(countryCode?: string): Promise<ResourceProvinces[]> {
    if (countryCode) {
      let promise = this.promiseProvincesForEachCountry.get(countryCode);

      if (!promise) {
        promise = this.promiseProvince
            .then((value) =>
                value.filter(x => !x.CountryCode || x.CountryCode?.toLowerCase() === countryCode.toLowerCase())
            );

        this.promiseProvincesForEachCountry.set(countryCode, promise);
      }

      return promise;
    }

    return this.promiseProvince;
  }

  static setCountries(countries: ResourceCountry[]): void {
    if (this.loadedCountries) {
      this.loadedCountries.length = 0;

      for (const country of countries) {
        this.loadedCountries.push(country);
      }
      this.addEmptyCountryField();
    } else {
      this.loadedCountries = countries;
      this.addEmptyCountryField();

      //@ts-ignore
      this.resolveCountryFunction(this.loadedCountries);
    }
  }

  static setProvinces(provinces: ResourceProvinces[]): void {
    if (this.loadedProvinces) {
      this.loadedProvinces.length = 0;

      for (const province of provinces) {
        this.loadedProvinces.push(province);
      }
      this.addEmptyProvinceField();
    } else {
      this.loadedProvinces = provinces;
      this.addEmptyProvinceField();
      this.resolveProvinceFunction(this.loadedProvinces);
    }
  }

  static addEmptyCountryField(): void {
    const emptyField = {
      Id: 0,
      IsPriority: false,
      IsoCode: '',
      Name: '-',
      PhonePrefix: ''
    };

    this.loadedCountries.unshift(emptyField);
  }

  static addEmptyProvinceField(): void {
    const emptyField = {
      Code: '',
      CountryCode: '',
      Text: '-'
    };

    this.loadedProvinces.unshift(emptyField);
  }

}
